import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const VIDEO_URL =
  "https://customer-assets-0z36b82j.emergentagent.net/job_193d628d-c2fe-4cef-a8c0-b6e57688a455/artifacts/x9v770dm_Holdstill%20Show%20Reel.mp4";

// The showreel is the canvas of the whole page: it runs fixed behind every
// section. Sections float over it as 3D panels; divider "windows" reveal it.
export const FixedVideoBackground = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const { scrollYProgress } = useScroll();

  // Slow cinematic zoom across the entire page scroll
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.play().catch(() => {});
  }, []);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  return (
    <>
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-[color:var(--hs-bg-0)]" aria-hidden="true">
        <motion.div className="absolute inset-0 transform-gpu will-change-transform" style={{ scale }}>
          {/* No poster: the charcoal page background shows while the stream
              buffers, then the showreel fades in smoothly. */}
          <video
            ref={videoRef}
            data-testid="hero-showreel-video"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-out ${
              videoReady ? "opacity-100" : "opacity-0"
            }`}
            src={VIDEO_URL}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onPlaying={() => setVideoReady(true)}
            onLoadedData={() => setVideoReady(true)}
          />
        </motion.div>
        {/* Base cinematic grade so overlaid text always stays readable */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(11,12,14,0.5)_0%,rgba(11,12,14,0.35)_40%,rgba(11,12,14,0.6)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(1200px_700px_at_20%_10%,rgba(202,255,26,0.08),transparent_55%)]" />
      </div>

      {/* Sound toggle — the showreel is the soundtrack of the page */}
      <button
        onClick={toggleMute}
        data-testid="hero-sound-toggle"
        aria-label={muted ? "Unmute showreel" : "Mute showreel"}
        className="fixed bottom-5 left-5 z-50 h-11 w-11 rounded-full border border-white/20 bg-black/50 backdrop-blur-md flex items-center justify-center text-white hover:border-[color:var(--hs-lime)] hover:text-[color:var(--hs-lime)] btn-lift"
      >
        {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </button>
    </>
  );
};
